import { cn } from "@ibada/ui"

export type OverviewMetricWidgetProps = {
  /** The figure, already formatted by the caller (money, count, percentage). */
  value: string
  /** Colour for the figure — direction/tone is the caller's judgement, not this widget's. */
  tone?: string
  /**
   * The denominator this figure is a share OF, when one honestly exists.
   *
   * A bare "586" makes the reader do arithmetic against a number on another card; "586 · 75%" does not.
   * Omitted deliberately where there is no denominator — a total is not a share of anything, and a
   * percentage against an invented denominator is worse than none because it reads as precise.
   */
  sharePercentage?: number | null
  /** The line under the figure. */
  helper?: string
  className?: string
}

/**
 * ONE figure, as a dashboard widget renders it.
 *
 * Deliberately card-less: the dashboard engine's `WidgetContainer` already provides the card, the icon, the
 * title and the ⋯ menu. Wrapping the value in another surface (which is what a *tiles grid* does, since each
 * tile draws its own) puts a box inside a box.
 *
 * Lifted out of the tenant's dashboard so the admin platform board renders identically — the two boards are
 * the same product idea pointed at different scopes, and they should not drift apart visually.
 */
export function OverviewMetricWidget({
  value,
  tone = "text-foreground",
  sharePercentage,
  helper,
  className,
}: OverviewMetricWidgetProps) {
  return (
    <div className={cn("min-w-0 space-y-2", className)}>
      <div className='flex min-w-0 items-baseline gap-2'>
        <p className={cn("truncate text-3xl font-semibold tracking-tight tabular-nums", tone)}>{value}</p>
        {sharePercentage !== null && sharePercentage !== undefined ? (
          <span className='text-muted-foreground shrink-0 text-sm font-medium tabular-nums'>
            {sharePercentage}%
          </span>
        ) : null}
      </div>
      {helper ? <p className='text-muted-foreground line-clamp-3 text-xs leading-relaxed'>{helper}</p> : null}
    </div>
  )
}
